import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import VerificationInput from 'react-verification-input';
import styled from 'styled-components';

import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { Text } from '../../../components/Text';
import { ForgotPasswordTokenDto } from '../../../model/user/dto/ForgotPasswordTokenDto';
import { SignupDto } from '../../../model/user/dto/SignupDto';
import { useAppDispatch } from '../../../redux/store';
import { userActions } from '../../../redux/user/user.slice';
import { Rounded } from '../../../theme/Rounded';
import { Spacing } from '../../../theme/Spacing';
import { AtiraToast } from '../../../utils/AtiraToast';
import { onSetTypedCRUDErrors } from '../../../utils/onSetTypedCRUDErrors';
import { ForgotPasswordBackStepButton } from './ForgotPasswordBackStepButton';

const InputWrapper = styled(Flex)<{ valid: boolean }>`
  flex-direction: column;
  gap: ${Spacing.s};
  width: 100%;
  margin-top: ${Spacing.xl};

  .vi__container {
    width: 100% !important;
  }

  .vi__character {
    border: ${({ theme, valid }) =>
      `2px solid ${valid ? `${theme.fgSecondary}30` : theme.danger}`} !important;
    background-color: ${({ theme }) => theme.transparent} !important;
    border-radius: ${Rounded.lg} !important;
  }
`;

const StyledButton = styled(Button)`
  height: 3.5rem;
  width: 100%;
  background-color: ${({ theme }) => theme.main};
  transition: background-color 0.2s;
  margin: 0;
  margin-top: ${Spacing.m};
  border-radius: ${Rounded.lg};

  &:hover {
    background-color: ${(props) => props.theme.lightMain};
  }
`;

const Wrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  gap: ${Spacing.s};
  margin-top: ${Spacing.xl};
  padding-inline: ${Spacing.s};
`;

const ResendEmailButton = styled(Button)`
  margin: 0;
  padding: 0;
  background-color: ${({ theme }) => theme.transparent};
  color: #4285f4;
  text-decoration: underline;
`;

type ForgotPasswordTokenProps = {
  nextStep: VoidFunction;
  prevStep: VoidFunction;
};

export const ForgotPasswordToken: React.FC<ForgotPasswordTokenProps> = ({
  nextStep,
  prevStep,
}) => {
  const [loading, setLoading] = useState(false);
  const [resendButtonEnabled, setResendButtonEnabled] = useState(true);

  const { control, getValues, setError } = useFormContext<
    ForgotPasswordTokenDto & { email: SignupDto['email'] }
  >();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const onNext = async () => {
    try {
      setLoading(true);
      await dispatch(
        userActions.forgotPasswordToken({ token: getValues('token') }),
      ).unwrap();

      AtiraToast.success(t('resetpassword.token.success'));
      nextStep();
    } catch (e: any) {
      AtiraToast.apiError(e, { autoClose: 6000 });
      console.log(e);

      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  const onResendEmail = async () => {
    try {
      await dispatch(
        userActions.forgotPasswordEmail({ email: getValues('email') }),
      ).unwrap();

      AtiraToast.success(t('resetpassword.email.success'));
      setResendButtonEnabled(false);
    } catch (e: any) {
      AtiraToast.apiError(e, { autoClose: 6000 });
      console.log(e);
    }
  };

  return (
    <Wrapper>
      <Flex flexDirection="column" gap="l" width={'100%'}>
        <ForgotPasswordBackStepButton onClick={prevStep} />

        <Text>{t('resetpassword.token.title')}</Text>

        <Text color="fgSecondary">{t('resetpassword.token.description')}</Text>
      </Flex>

      <Controller
        name="token"
        control={control}
        rules={{ required: true, minLength: 6 }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <InputWrapper valid={!error}>
            <VerificationInput
              placeholder=""
              onChange={onChange}
              value={value}
            />

            {error?.message ? (
              <Text fontSize="s" color="danger">
                {error.message}
              </Text>
            ) : null}
          </InputWrapper>
        )}
      />
      <StyledButton onClick={onNext} loading={loading}>
        {t('resetpassword.token.btn')}
      </StyledButton>

      <Flex justifyContent="center" alignItems="center" gap="s" marginTop="xl">
        <Text color="fgMuted">{t('resetpassword.token.resend.paragraph')}</Text>

        <ResendEmailButton
          onClick={onResendEmail}
          disabled={!resendButtonEnabled}
        >
          {t('resetpassword.token.resend.btn')}
        </ResendEmailButton>
      </Flex>
    </Wrapper>
  );
};
